/** INV.3 height model — keep in lockstep with functions/warehouse/slotGeometry.js */

export const ITEM_CLEARANCE_IN = 1;
export const DEFAULT_BASE_IN = 12;
export const DEFAULT_BEAM_IN = 1.75;
export const DEFAULT_LEVEL_CLEARS = [24, 18, 14, 12];
export const DEFAULT_WIDTH_IN = 24;
export const DEFAULT_DEPTH_IN = 18;
export const DEFAULT_CAPACITY_EQ = 80;

export const REACH_BANDS = {
  stoop: { id: "stoop", label: "Stoop", min: 0, max: 24, fill: "#e8eaee" },
  golden: { id: "golden", label: "Golden", min: 24, max: 60, fill: "#fef3c7" },
  overhead: { id: "overhead", label: "Overhead", min: 60, max: 72, fill: "#fde8d2" },
  ladder: { id: "ladder", label: "Ladder", min: 72, max: 400, fill: "#fce4ea" },
};

export const clampNum = (n, min, max, fallback, decimals = 2) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return fallback;
  const capped = Math.max(min, Math.min(max, v));
  const f = 10 ** decimals;
  return Math.round(capped * f) / f;
};

export const defaultLevelClears = (levels) => {
  const n = Math.max(1, Math.min(8, Number(levels) || 1));
  const last = DEFAULT_LEVEL_CLEARS[DEFAULT_LEVEL_CLEARS.length - 1];
  return Array.from({ length: n }, (_, i) => DEFAULT_LEVEL_CLEARS[i] ?? last);
};

export const padLevelClears = (raw, levels) => {
  const defaults = defaultLevelClears(levels);
  const src = Array.isArray(raw) ? raw : [];
  return defaults.map((d, i) => {
    const v = Number(src[i]);
    return Number.isFinite(v) && v > 0 ? clampNum(v, 1, 120, d) : d;
  });
};

export const reachBand = (midpointIn) => {
  const m = Number(midpointIn);
  if (!Number.isFinite(m) || m < 24) return "stoop";
  if (m <= 60) return "golden";
  if (m <= 72) return "overhead";
  return "ladder";
};

export const deriveLevels = ({
  baseIn = DEFAULT_BASE_IN,
  beamIn = DEFAULT_BEAM_IN,
  clearHeights = DEFAULT_LEVEL_CLEARS,
} = {}) => {
  const base = clampNum(baseIn, 0, 60, DEFAULT_BASE_IN);
  const beam = clampNum(beamIn, 0.25, 8, DEFAULT_BEAM_IN);
  const clears = (clearHeights || []).map((h) =>
    clampNum(h, 1, 120, DEFAULT_LEVEL_CLEARS[0])
  );
  const levels = [];
  let elevationIn = base;
  for (let i = 0; i < clears.length; i += 1) {
    const clearHeightIn = clears[i];
    const topOfLevelIn = clampNum(
      elevationIn + clearHeightIn,
      0,
      400,
      elevationIn + clearHeightIn
    );
    const midpointIn = (elevationIn + topOfLevelIn) / 2;
    const band = reachBand(midpointIn);
    levels.push({
      level: i + 1,
      clearHeightIn,
      elevationIn: clampNum(elevationIn, 0, 400, elevationIn),
      topOfLevelIn,
      midpointIn: clampNum(midpointIn, 0, 400, midpointIn),
      maxItemHeightIn: Math.max(0, clearHeightIn - ITEM_CLEARANCE_IN),
      reachBand: band,
      isGoldenZone: band === "golden",
    });
    elevationIn = topOfLevelIn + beam;
  }
  const last = levels[levels.length - 1];
  const rackHeightIn = last
    ? clampNum(last.topOfLevelIn + beam, 0, 400, last.topOfLevelIn + beam)
    : base;
  return {
    baseIn: base,
    beamIn: beam,
    levels,
    rackHeightIn,
    goldenLevels: levels.filter((l) => l.isGoldenZone).map((l) => l.level),
  };
};

export const maxItemHeightIn = (clearHeightIn) =>
  Math.max(0, Number(clearHeightIn) - ITEM_CLEARANCE_IN || 0);
